import { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { BrandContext } from "../../App";

import { LiveKitManager } from "../../components/complex/LiveKitManager";
import { MediaManager } from "../../services/media/MediaManager";

import CallToolbar from "../simple/CallToolbar";

import CallScreenSidePanel from "../complex/CallScreenSidePanel";

import { PlayCircleFilledOutlined } from "@mui/icons-material";

import LiveCaptions from "../CallScreen/LiveCaptions";

import Cookies from "js-cookie";

import { useAPI } from "/src/hooks/useAPI";

import { useUI } from "../../contexts/UIContext";
import { useAuth } from "../../contexts/AuthContext";

import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
} from "@mui/material";

import { VideoContainer } from "./styles.js";

type VideoAvatarProps = {
  config: string[];
  scenario: string[];
  autoConnect: boolean;
  firstScenario: boolean;
  isFeedbackSession: boolean;
  startFeedbackSimulation?: () => void;
  setFeedbackSimulationError?: (error: string) => void;
};

// determine external mode
const determineExternalMode = (scenarioData, pathname) => {
  const isExternalRoute = pathname.includes("/externalscenario");
  return isExternalRoute && scenarioData?.external_scenario === true;
};

const VideoAvatar = ({
  config,
  scenario,
  autoConnect = true,
  firstScenario = null,
  isFeedbackSession = false,
  startFeedbackSimulation = () => {},
  setFeedbackSimulationError = (error: string) => {},
}: VideoAvatarProps) => {
  const queryClient = useQueryClient();
  const { brand, trainingProgram } = useContext(BrandContext);

  const { authToken } = useAuth();
  const { theme, setShowCrumbs, isMobile } = useUI();

  const avatarVideoRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const EDGE_MARGIN = 16; // px spacing from edges

  const [transcript, setTranscript] = useState([]);
  const [liveKitManager, setLiveKitManager] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [roomName, setRoomName] = useState("");
  const [simulationId, setSimulationId] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);

  const [hasCompletedSim, setHasCompletedSim] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [subTitlesEnabled, setSubTitlesEnabled] = useState(false);

  const [videoPosition, setVideoPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const { callAPI } = useAPI();

  const media = new MediaManager();

  const isExternalMode = determineExternalMode(
    scenario,
    window.location.pathname
  );

  const resourcesExist = scenario?.resources && scenario.resources.length >= 1;

  const [showSidePanel, setShowSidePanel] = useState(resourcesExist);

  const [onboardingOpen, setOnboardingOpen] = useState(
    firstScenario == null ? true : firstScenario
  );

  const [panelValue, setPanelValue] = useState(resourcesExist ? 1 : 0);

  const handleToggleFullscreen = () => {
    const element = containerRef.current;

    if (!element) return;

    if (!document.fullscreenElement) {
      // Enter Fullscreen
      element.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      // Exit Fullscreen
      document.exitFullscreen();
    }
  };

  // 4. Add a listener to update state if user presses ESC
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const getClient = (event) => {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const handleDown = (event) => {
    const { x, y } = getClient(event);
    setIsDragging(true);
    startXRef.current = x - videoPosition.x;
    startYRef.current = y - videoPosition.y;
  };

  const handleMouseDown = (event) => {
    setIsDragging(true);
    startXRef.current = event.clientX - videoPosition.x;
    startYRef.current = event.clientY - videoPosition.y;
  };

  useEffect(() => {
    if (!isFeedbackSession) setShowCrumbs(false);
    const manager = new LiveKitManager(trackHandler);

    manager.setTranscriptHandler((raw) => {
      const parsed = JSON.parse(raw);
      setTranscript((prev) => [...prev, parsed]);
    });

    setLiveKitManager(manager);

    if (
      Cookies.get("GN-CompletedSim") ||
      window.location.href ===
        "https://app.gurunow.ai/externalscenario/4fdb30f6-d89f-4af3-a56c-ae10c949cb43"
        || window.location.href === "http://localhost:3000/externalscenario/6d8a6c79-9836-482e-a9e6-8a2d1964fcec"
    ) {
        
      setHasCompletedSim(true);
    }

    console.log("IN WINDOW?", window.location.href==="http://localhost:3000/externalscenario/6d8a6c79-9836-482e-a9e6-8a2d1964fcec")

    // cleanup on unmount
    return () => {
      setShowCrumbs(true);
      // liveKit cleanup
      manager.cleanup();
      const remoteAudio = document.getElementById("remote-audio");
      if (remoteAudio) remoteAudio.remove();
    };
  }, []);

  // connect automatically if autoConnect is true
  useEffect(() => {
    if (
      avatarVideoRef.current &&
      autoConnect &&
      liveKitManager &&
      !isConnected &&
      !isConnecting
    ) {
      connectToLiveKit();
    }
  }, [autoConnect, liveKitManager, isConnected, isConnecting, avatarVideoRef]);

  const trackHandler = useCallback(({ track, streams, participant }) => {
    console.log("Track handler:", { track, streams, participant });

    // --- CHANGED SECTION START ---
    if (track.kind === "video") {
      // Don't attach manually here. Just save it to state.
      // React will handle the attachment in the useEffect below.
      setRemoteVideoTrack(track);
      console.log("Remote video track received and stored in state");
    }
    // --- CHANGED SECTION END ---

    // Keep your existing Audio logic exactly as it is
    if (track.kind === "audio") {
      let audioElement = document.getElementById("remote-audio");
      if (!audioElement) {
        audioElement = document.createElement("audio");
        audioElement.id = "remote-audio";
        audioElement.autoplay = true;
        document.body.appendChild(audioElement);
      }
      const stream = streams[0];
      if (stream) {
        audioElement.srcObject = stream;
      } else {
        audioElement.srcObject = new MediaStream([track.mediaStreamTrack]);
      }
    }
  }, []);

  // Add this new useEffect to your component
  useEffect(() => {
    const element = avatarVideoRef.current;

    if (remoteVideoTrack && element) {
      console.log("Attaching video track via Effect");

      // LiveKit's attach() handles srcObject, autoplay, and playsinline logic
      remoteVideoTrack.attach(element);
    }

    // Cleanup function: important to detach when component unmounts
    // or when the track changes to prevent memory leaks
    return () => {
      if (remoteVideoTrack) {
        remoteVideoTrack.detach();
      }
    };
  }, [remoteVideoTrack]); // Dependencies: runs when track changes OR component remounts

  const connectToLiveKit = async () => {
    if (!liveKitManager) {
      if (isConnecting || !config.id) {
        console.log("==> Cannot connect to LiveKit");
        return;
      }
    }
    setIsConnecting(true);
    setError(null);

    try {
      if (isExternalMode && scenario?.useProvidedToken) {
        console.log("==> Using provided token for external scenario");
        if (scenario.simulation_id) {
          setSimulationId(scenario.simulation_id);
          setRoomName(scenario.simulation_id || "LiveKit-Room");
        } else {
          console.warn("No simulation_id provided for external simulation");
        }

        if (!scenario.livekit_url) {
          throw new Error("No LiveKit URL provided for external simulation");
        }

        await liveKitManager.connect(scenario.livekit_url, {
          token: scenario.token,
        });
        setIsConnected(true);
        console.log(
          "==> Connected to LiveKit successfully with provided token"
        );
        await handleEnableAudio();
        setupCamera();
        return;
      }

      // regular scenarios
      const start_simulation_endpoint = "start_simulation";
      const data = config;

      if (!data["token"]) {
        throw new Error("No token or URL received from server");
      }

      setSimulationId(data.id);
      setRoomName(data.id || "LiveKit-Room");

      await liveKitManager.connect(data["livekit_url"], {
        token: data["token"],
      });
      setIsConnected(true);
      console.log("==> Connected to LiveKit successfully");

      // enable audio when connected
      await handleEnableAudio();

      setupCamera();
      setIsLoading(false);
    } catch (error) {
      console.error("LiveKit connection error:", error);
      setError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleEnableAudio = async () => {
    if (liveKitManager) {
      try {
        await liveKitManager.setMicrophoneEnabled(true);
        setAudioEnabled(true);
      } catch (error) {
        console.error("Error enabling microphone:", error);
        setError("Failed to enable audio: " + error.message);
      }
    }
  };
  const setupCamera = () => {
    if (!media) return;

    console.log("==> Setting up webcam");

    media.setupLocalVideo(videoRef);

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        console.log("==> Webcam access granted");
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setVideoEnabled(true);
      })
      .catch((err) => {
        console.error("==> Webcam access error:", err);
        setVideoEnabled(false);
      });
  };

  const handlePlayFeedbackSimulation = async () => {
    try {
      await startFeedbackSimulation();
    } catch (err) {
      console.error("Error starting feedback simulation:", err);
      setFeedbackSimulationError(
        "Failed to start feedback simulation: " + err.message
      );
    }
  };
  useEffect(() => {
    // If in feedback session then connect once the config is set (the user has to click to "Start Feedback" first)
    // And only connect if there's no simulationId yet (to avoid double connections)
    if (isFeedbackSession && !simulationId) {
      connectToLiveKit();
    }
  }, [config]);

  // On initial mount, if not autoConnecting, set isLoading to false
  useEffect(() => {
    if (!autoConnect) {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (videoEnabled && videoRef.current && !videoRef.current.srcObject) {
      console.log("==> Ensuring video stream is attached after render");

      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
        })
        .catch((err) => {
          console.error("==> Webcam access error during post-render fix:", err);
        });
    }
  }, [videoEnabled, isConnected, isLoading, isConnecting]);

  useEffect(() => {
    const handleMove = (event) => {
      if (!isDragging) return;
      const { x, y } = getClient(event);
      const newX = x - startXRef.current;
      const newY = y - startYRef.current;
      setVideoPosition({ x: newX, y: newY });
    };

    const handleUp = () => {
      if (!isDragging) return;
      setIsDragging(false);

      const videoEl = videoRef.current;
      if (!videoEl) return;

      const rect = videoEl.getBoundingClientRect();
      const isVideoElement = videoEl.id === "video"; // Check if it's the video or avatar
      let videoWidth = isVideoElement ? 182 : 182; // Desktop sizes from sx prop
      let videoHeight = isVideoElement ? 182 : 165; // Desktop sizes from sx prop

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (isMobile) {
        // Mobile: Snap to window corners, adjust for mobile sizes
        let mobileVideoWidth = isVideoElement ? 103 : 103; // Mobile sizes from sx prop
        let mobileVideoHeight = isVideoElement ? 103 : 96; // Mobile sizes from sx prop
        if (isFeedbackSession) {
          mobileVideoWidth = 103;
          mobileVideoHeight = 93;
        }
        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN }, // top-left
          { x: vw - mobileVideoWidth - EDGE_MARGIN, y: EDGE_MARGIN }, // top-right
          { x: EDGE_MARGIN, y: vh - (mobileVideoHeight + 70) - EDGE_MARGIN }, // bottom-left
          {
            x: vw - mobileVideoWidth - EDGE_MARGIN,
            y: vh - (mobileVideoHeight + 70) - EDGE_MARGIN,
          }, // bottom-right
        ];

        let closest = corners[0];
        let minDist = Infinity;

        corners.forEach((corner) => {
          const dx = corner.x - videoPosition.x;
          const dy = corner.y - videoPosition.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist) {
            minDist = dist;
            closest = corner;
          }
        });

        setVideoPosition(closest);
      } else {
        // Desktop: Snap to callscreen-container corners
        const containerEl = document.getElementById("callscreen-container");
        if (!containerEl) return;

        const containerRect = containerEl.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Snap points relative to the container's top-left corner with EDGE_MARGIN
        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN }, // top-left
          { x: containerWidth - videoWidth - EDGE_MARGIN, y: EDGE_MARGIN }, // top-right
          { x: EDGE_MARGIN, y: containerHeight - videoHeight - EDGE_MARGIN }, // bottom-left
          {
            x: containerWidth - videoWidth - EDGE_MARGIN,
            y: containerHeight - videoHeight - EDGE_MARGIN,
          }, // bottom-right
        ];

        // Ensure snap points stay within container bounds
        const boundedCorners = corners.map((corner) => ({
          x: Math.max(0, Math.min(corner.x, containerWidth - videoWidth)),
          y: Math.max(0, Math.min(corner.y, containerHeight - videoHeight)),
        }));

        // Find the closest corner
        let closest = boundedCorners[0];
        let minDist = Infinity;

        boundedCorners.forEach((corner) => {
          // Adjust videoPosition to container-relative coordinates for distance calculation
          const containerRelativeX = videoPosition.x - containerRect.left;
          const containerRelativeY = videoPosition.y - containerRect.top;
          const dx = corner.x - containerRelativeX;
          const dy = corner.y - containerRelativeY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist) {
            minDist = dist;
            closest = corner;
          }
        });

        // Set position relative to the container
        setVideoPosition({
          x: closest.x,
          y: closest.y,
        });
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, videoPosition]);

  const handleToggleVideo = async () => {
    const newState = !videoEnabled;
    setVideoEnabled(newState);

    if (!media) return;

    if (newState) {
      // Attempt to turn ON webcam
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Failed to enable webcam:", err);
        setVideoEnabled(false);
      }
    } else {
      // Turn OFF webcam
      const currentStream = videoRef.current?.srcObject;
      if (currentStream && currentStream.getTracks) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };
  const handleDisconnect = useCallback(() => {
    if (liveKitManager) {
      liveKitManager.cleanup();
      setIsConnected(false);
      setAudioEnabled(false);
    }
  }, [liveKitManager]);

  const handleEndSimulation = useCallback(async () => {
    const scenarioData = scenario;
    const effectiveSimulationId = simulationId || scenarioData?.simulationId;
    if (!effectiveSimulationId) {
      setError("No active simulation to end");
      return;
    }

    // endpoint determination
    let end_simulation_endpoint = isExternalMode
      ? "rtc_end_external_simulation"
      : "rtc_end_simulation";

    try {
      await callAPI(`${end_simulation_endpoint}`, {
        method: "POST",
        token: authToken,
        body: {
          simulation_id: effectiveSimulationId,
        },
      });

      // Invalidate training programs cache to get fresh data
      queryClient.invalidateQueries({ queryKey: ["trainingPrograms"] });
      queryClient.invalidateQueries({ queryKey: ["simulations"] });

      handleDisconnect();
      setSimulationId(null);

      // redirection logic
      if (isExternalMode) {
        // external mode redirection logic
        const redirectPath = scenarioData.has_scoring_engine
          ? `/externalscenario_feedback/${effectiveSimulationId}`
          : `/externalscenario/${scenarioData.id}`;
        window.location.href = redirectPath;
      } else {
        // TODO: remove hack; add proper scenario type to scenarioData
        const isLesson =
          scenarioData.resources !== null &&
          Array.isArray(scenarioData.resources);
        if (isLesson) {
          let targetScenarioId = null;
          if (trainingProgram?.steps) {
            let currentFound = false;
            outerLoop: for (const step of trainingProgram.steps) {
              const chapter = step.chapter;
              if (!chapter?.steps) continue;
              for (const scenarioStep of chapter.steps) {
                const scenario = scenarioStep.scenario;
                if (scenario.id === scenarioData.id) {
                  currentFound = true;
                  continue;
                }
                if (!currentFound) {
                  continue;
                }
                targetScenarioId = scenario.id;
                break outerLoop;
              }
            }
          }
          if (targetScenarioId) {
            window.location.href = `/dashboard/simulate/${targetScenarioId}`;
          } else {
            // Redirect to the program view if the lesson was the last scenario in the program
            window.location.href = `/dashboard/trainingprogram/${trainingProgram.id}`;
          }
        } else if (authToken && !isFeedbackSession) {
          window.location.href = `/dashboard/feedback/${effectiveSimulationId}`;
        } else if (authToken && isFeedbackSession) {
          // Do not redirect
        } else {
          // redirect to login or home
          console.warn(
            "No authentication token available for internal mode redirect"
          );
          window.location.href = "/login"; // or another appropriate page
        }
      }
    } catch (error) {
      console.error(
        "Error determining redirect after ending simulation:",
        error
      );
      setError(`Failed to redirect after simulation: ${error.message}`);
    }
  }, [
    simulationId,
    scenario,
    isExternalMode,
    brand.api_base_url,
    authToken,
    handleDisconnect,
  ]);

  const BoxLoader = (props) => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(35, 31, 32, 0.85)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 20,
      }}
    >
      <CircularProgress size={60} sx={{ color: "#ffffff" }} />
      <Typography variant="h5" mt={3} color="white">
        {props.message || "Connecting..."}
      </Typography>
    </Box>
  );

  const handleToggleSubtitles = () => {
    setSubTitlesEnabled(!subTitlesEnabled);
  };

  console.log("scenario", scenario);

  return (
    <VideoContainer
      ref={containerRef}
      id="callscreen-container"
      className={autoConnect ? "full" : "mini"}
    >
      {!isMobile && !isFeedbackSession && (
        <div className="sim-name-overlay">{scenario?.name}</div>
      )}
      {autoConnect && (isLoading || isConnecting || !isConnected) ? (
        <BoxLoader />
      ) : (
        <>
          {!autoConnect && !isConnected && (
            <>
              {isConnecting || isLoading ? (
                <BoxLoader
                  message={isConnecting ? "Connecting..." : "Loading..."}
                />
              ) : (
                <button
                  onClick={async () => {
                    if (isFeedbackSession) {
                      setIsLoading(true);
                      await handlePlayFeedbackSimulation();
                      setIsLoading(false);
                    } else {
                      connectToLiveKit();
                    }
                  }}
                  id="manual-connect-button"
                >
                  <PlayCircleFilledOutlined />
                </button>
              )}
            </>
          )}
          {isConnected && (
            <>
              <Paper
                className="video-inner-container"
                elevation={3}
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "1350px",
                  aspectRatio: "16 / 9",
                  borderRadius: 2,
                  backgroundColor: "#231F20",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  overflow: "hidden",
                  left: "0",
                  margin: "0 auto",
                  filter: !hasCompletedSim ? "blur(2px)" : "none",
                  zIndex: !hasCompletedSim ? "99" : "auto",
                  [theme.breakpoints.down("md")]: !isFeedbackSession
                    ? {
                        height: "calc(100vh - 70px)",
                        aspectRatio: "9 / 16",
                        borderRadius: 0,
                        position: "absolute",
                        bottom: 0,
                        transition: "all 0.2s ease",
                      }
                    : {}, // fallback if isFeedbackSession is true
                  "@media (max-width: 740px) and (orientation: landscape) and (pointer: coarse)":
                    {
                      height: !isFeedbackSession ? "100vh" : "auto",
                    },
                }}
              >
                {videoEnabled ? (
                  <video
                    ref={videoRef}
                    id="video"
                    draggable={false}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      position: "absolute",
                      top: videoPosition.y,
                      left: videoPosition.x,
                      objectFit: "cover",
                      borderRadius: "8px",
                      cursor: isDragging ? "grabbing" : "grab",
                      backgroundColor: "black",
                      zIndex: 500,
                      display:
                        isConnected && !isLoading && !isConnecting
                          ? "block"
                          : "none",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                      transition: isDragging ? "none" : "top 0.2s, left 0.2s", // smooth snap
                      transform: "rotateY(180deg)",
                      WebkitTransform:
                        "rotateY(180deg)" /* Safari and Chrome */,
                      MozTransform: "rotateY(180deg)",
                    }}
                    onMouseDown={handleDown}
                    onTouchStart={handleDown}
                  />
                ) : (
                  <Avatar
                    id="avatar-pic"
                    onMouseDown={handleMouseDown}
                    sx={{
                      position: "absolute",
                      top: videoPosition.y,
                      left: videoPosition.x,
                      width: "182px",
                      height: "165px",
                      cursor: isDragging ? "grabbing" : "grab",
                      borderRadius: "8px",
                      backgroundColor: "black",
                      zIndex: 500,
                      display:
                        isConnected && !isLoading && !isConnecting
                          ? "flex"
                          : "none",
                      fontSize: "48px",
                      color: "grey",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                    }}
                  />
                )}

                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    gap: 15,
                    justifyContent: showSidePanel ? "space-between" : "center",
                    alignItems: "flex-end",
                    zIndex: 30,
                    boxSizing: "border-box",
                    padding: `${theme.customSpacing["2xl"]}`,
                  }}
                >
                  <LiveCaptions
                    transcript={transcript}
                    enabled={subTitlesEnabled}
                    isMobile={isMobile}
                  />

                  <CallToolbar
                    videoEnabled={videoEnabled}
                    handleToggleVideo={handleToggleVideo}
                    handleEndSimulation={handleEndSimulation}
                    openHelpModal={() => setOnboardingOpen(true)}
                    panelValue={panelValue}
                    setPanelValue={setPanelValue}
                    openSidePanel={() => setShowSidePanel(true)}
                    scenarioData={scenario}
                    isExternalMode={isExternalMode}
                    isMobile={isMobile}
                    isFeedbackSession={isFeedbackSession}
                    onHelpTooltipClose={() => setHasCompletedSim(true)}
                    handleToggleFullscreen={handleToggleFullscreen}
                    isFullscreen={isFullscreen}
                    subTitlesEnabled={subTitlesEnabled}
                    handleToggleSubtitles={handleToggleSubtitles}
                  />

                  {showSidePanel && (
                    <CallScreenSidePanel
                      scenarioData={scenario}
                      isExternalMode={isExternalMode}
                      tab={panelValue}
                      showSidePanel={showSidePanel}
                      onClose={() => setShowSidePanel(false)}
                      transcript={transcript}
                      isMobile={isMobile}
                    />
                  )}
                </Box>
              </Paper>
            </>
          )}
        </>
      )}
      <video ref={avatarVideoRef} id="lk-video" autoPlay playsInline muted />
    </VideoContainer>
  );
};

export default VideoAvatar;
